import type { CaptureDiagnosticName } from '../audio/captureDiagnostics';
const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
const QUEUE_KEY = 'luma-measurement-outbox-v2:';

export type CanonicalEventName = CaptureDiagnosticName
  | 'capture_started'
  | 'mic_check_completed'
  | 'take_persisted'
  | 'verbatim_persisted'
  | 'capture_completed'
  | 'capture_cancelled'
  | 'playback_started'
  | 'playback_progress'
  | 'playback_completed'
  | 'playback_cancelled'
  | 'offline_playback_synced';

export interface MeasurementEvent {
  event_name: CanonicalEventName;
  idempotency_key?: string;
  occurred_at?: string;
  household_id?: string;
  listener_id?: string;
  render_id?: string;
  voice_id?: string;
  session_id?: string;
  attempt_id?: string;
  online_state?: 'online' | 'offline' | 'unknown';
  delayed_sync?: boolean;
  progress_ratio?: number;
  properties?: Record<string, string | number | boolean | null>;
}

interface QueuedEvent extends MeasurementEvent {
  idempotency_key: string;
  occurred_at: string;
}

function eventId(): string {
  return globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

interface AccountScope { userId: string; token: string }
function accountScope(): AccountScope | null {
  try {
    const userId = localStorage.getItem('user_id');
    const token = localStorage.getItem('auth_token');
    return userId && token ? { userId, token } : null;
  } catch { return null; }
}
function currentScope(scope: AccountScope): boolean {
  const current = accountScope();
  return current?.userId === scope.userId && current?.token === scope.token;
}
function readQueue(scope: AccountScope): QueuedEvent[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(QUEUE_KEY + scope.userId) || '[]');
    return Array.isArray(parsed) ? parsed.slice(-200) : [];
  } catch { return []; }
}
function writeQueue(scope: AccountScope, events: QueuedEvent[]): void {
  if (!currentScope(scope)) return;
  try { localStorage.setItem(QUEUE_KEY + scope.userId, JSON.stringify(events.slice(-200))); }
  catch { /* Storage-denied diagnostics must not affect playback or recording. */ }
}
async function send(event: QueuedEvent, scope: AccountScope): Promise<boolean> {
  if (!currentScope(scope)) return false;
  try {
    const response = await fetch(`${API_BASE_URL}/measurements/events`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${scope.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
    });
    return response.ok;
  } catch { return false; }
}

export async function emitMeasurementEvent(event: MeasurementEvent): Promise<void> {
  try {
    const scope = accountScope();
    if (!scope) return;
    const queued: QueuedEvent = {
      ...event,
      idempotency_key: event.idempotency_key ?? `client:${eventId()}`,
      occurred_at: event.occurred_at ?? new Date().toISOString(),
      online_state: navigator.onLine ? 'online' : 'offline',
    };
    if (navigator.onLine && await send(queued, scope)) return;
    writeQueue(scope, [...readQueue(scope), { ...queued, delayed_sync: true }]);
  } catch { /* Measurement is optional. */ }
}

export async function flushMeasurementOutbox(): Promise<void> {
  if (!navigator.onLine) return;
  const scope = accountScope();
  if (!scope) return;
  const pending = readQueue(scope);
  for (const event of pending) {
    if (!currentScope(scope)) return;
    // Retain the original event name, timestamp and idempotency key on retries.
    if (await send({ ...event, delayed_sync: true }, scope)) {
      // Re-read so an event queued during this request is not overwritten.
      writeQueue(scope, readQueue(scope).filter((item) => item.idempotency_key !== event.idempotency_key));
    }
  }
}

export function createMeasurementSession(prefix: string): string {
  return `${prefix}:${eventId()}`;
}
